import {
  STUDENT_BASIC_FIELDS,
  STUDENT_ADDITIONAL_FIELDS,
  EMPTY_ARRAY,
  PROFILE_ADDITIONAL_FIELDS,
  ADDITIONAL_FIELD_UID_TO_CONFIG_UID_MAP,
} from "./constants";
import ACLStore from "lib/aclStore";
import _ from "lodash";
import { parseConfigurationValue } from "Utils";

const shouldDisplayAdditionalFieldInPersonalInfo = field => {
  return ACLStore.can(field.displayPerm);
};

const getLabelForPersonalInfoAdditionalField = field => {
  return parseConfigurationValue(ACLStore.getConfigObj(field.customLabelPerm));
};

const getStudentAdditionalFields = t => {
  return _.reduce(
    STUDENT_ADDITIONAL_FIELDS,
    (acc, fieldUid) => {
      const additionalInfo = _.get(PROFILE_ADDITIONAL_FIELDS, fieldUid, {});
      if (shouldDisplayAdditionalFieldInPersonalInfo(additionalInfo))
        acc.push({
          fieldUid,
          label: t(getLabelForPersonalInfoAdditionalField(additionalInfo)),
          configUid: ADDITIONAL_FIELD_UID_TO_CONFIG_UID_MAP[fieldUid],
        });
      return acc;
    },
    []
  );
};

const getStudentCustomFieldsConfig = customFieldsConfigData => {
  const data = _.get(customFieldsConfigData, "node", {});
  const customFieldsConfigString = _.get(
    data,
    ["globalConstants", 0, "value"],
    "{}"
  );
  return JSON.parse(customFieldsConfigString);
};

export const getStudentPersonalDetailsFields = ({
  t,
  customFieldsConfigData,
}) => {
  const studentCustomFieldsConfig = getStudentCustomFieldsConfig(
    customFieldsConfigData
  );
  const additionalFields = getStudentAdditionalFields(t);

  const updatedAdditionalFields = _.map(
    additionalFields,
    ({ fieldUid, label, configUid }) => ({
      fieldUid,
      label,
      fieldType: _.get(
        studentCustomFieldsConfig,
        [configUid, "fieldType"],
        "TextInput"
      ),
      options: _.get(
        studentCustomFieldsConfig,
        [configUid, "dropdownOptions"],
        []
      ),
      placeholder: t("common:enter_with_label", {
        label,
      }),
    })
  );

  return [...STUDENT_BASIC_FIELDS(t), ...updatedAdditionalFields];
};

export const getStudentPersonalDetailsFieldValue = ({
  studentPersonalDetails,
  studentBackendDetails,
  studentId,
}) => {
  const backendValues = _.reduce(
    studentBackendDetails.fields,
    (acc, item) => {
      acc[item.fieldUid] = {
        value: item.value,
        sourceValue: item.sourceValue,
      };
      return acc;
    },
    {}
  );

  const localOverrides = _.mapValues(
    _.get(studentPersonalDetails, studentId, {}),
    value => ({ value })
  );

  return _.merge({}, backendValues, localOverrides);
};

export const transformStudentPersonalDetailsToBackendFormat = ({
  studentPersonalDetails,
  studentId,
}) => {
  return _.map(studentPersonalDetails[studentId], (value, fieldUid) => ({
    fieldUid,
    value,
    studentId,
  }));
};

export const hasUnsavedChangesInAcademicRecords = ({ state }) => {
  const {
    studentPersonalDetails,
    studentTranscriptComments,
    studentGradeRecords,
    deletedRecordRows,
    newCourseRows,
    newCourseRatings,
    updatedCourseLevels,
    isEditMode,
    updatedCourseCodes,
  } = _.get(state, "transcriptV3.transcriptEditGrades", {});

  if (!isEditMode) {
    return false;
  }

  return (
    !_.isEmpty(studentPersonalDetails) ||
    !_.isEmpty(studentTranscriptComments) ||
    !_.isEmpty(studentGradeRecords) ||
    !_.isEmpty(deletedRecordRows) ||
    !_.isEmpty(newCourseRows) ||
    !_.isEmpty(newCourseRatings) ||
    !_.isEmpty(updatedCourseLevels) ||
    !_.isEmpty(updatedCourseCodes)
  );
};

export const getAcademicRecordsFromData = academicRecordsData =>
  _.get(academicRecordsData, "node.transcriptSummary.grades", EMPTY_ARRAY);

export const getCommentFieldIdFromData = commentFieldData =>
  _.get(commentFieldData, ["node", "transcriptCommentFieldsV3", 0, "id"]);

export const getTranscriptCoursesFromData = transcriptResourcesData =>
  _.get(transcriptResourcesData, "node.transcriptCourses", EMPTY_ARRAY);

export const getTranscriptLevelsFromData = transcriptResourcesData =>
  _.get(transcriptResourcesData, "node.transcriptLevels", EMPTY_ARRAY);

export const getAcrossYearAggregatesConfig = academicRecordsData => {
  const configuration = _.get(
    academicRecordsData,
    "node.transcriptSummary.configuration"
  );

  if (!configuration) return null;

  const parsedConfig = _.isString(configuration)
    ? JSON.parse(configuration)
    : configuration;

  return _.get(parsedConfig, "acrossYearAggregates", null);
};
